import { Button, Card, CardContent, CardHeader, CardTitle } from "@exhousing/design-system";
import { ArrowRight, ClipboardCheck } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { StatusBadge } from "@/components/StatusBadge";
import { useCreateInspection, useInspections } from "@/hooks/api/useInspections";
import type { components } from "@/types/api";

type Inspection = components["schemas"]["InspectionListItem"];

const KINDS = [
  {
    type: "check_in",
    title: "Check-in",
    blurb: "Record the property's condition at move-in — the baseline for the deposit.",
  },
  {
    type: "check_out",
    title: "Check-out",
    blurb:
      "Compare against the check-in at move-out; its findings become the deposit deductions.",
  },
] as const;

export function ContractInspections({ contractId }: { contractId: string }) {
  const { data: inspections, isLoading } = useInspections(contractId);
  const create = useCreateInspection();
  const navigate = useNavigate();

  if (isLoading) return null;
  const byType = (t: string): Inspection | undefined =>
    (inspections ?? []).find((i) => i.type === t);

  const start = (type: string) =>
    create.mutate(
      { contract_id: contractId, type },
      {
        onSuccess: (insp) => navigate(`/inspections/${insp.id}`),
        onError: (e) => toast.error(e.message),
      },
    );

  return (
    <div className="space-y-6">
      {KINDS.map((k) => {
        const insp = byType(k.type);
        return (
          <Card key={k.type}>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="size-4 text-accent" />
                {k.title}
              </CardTitle>
              {insp && <StatusBadge status={insp.status} />}
            </CardHeader>
            <CardContent className="flex flex-wrap items-center justify-between gap-3">
              <p className="max-w-prose text-sm text-muted-foreground">{k.blurb}</p>
              {insp ? (
                <Link
                  to={`/inspections/${insp.id}`}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                >
                  Open
                  <ArrowRight className="size-3.5" />
                </Link>
              ) : (
                <Button size="sm" disabled={create.isPending} onClick={() => start(k.type)}>
                  Start {k.title.toLowerCase()}
                </Button>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
